import { setCSS } from '@gimlite/common';
import { observer } from 'mobx-react-lite';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { useStoreUIV1 } from '../../hooks/useStore.hook';
import { ThemeStoreContextType } from '../../stores/theme.store';
import './apploader.component.scss';

const AppLoaderContext = createContext<{
  isLoading: boolean;
  setIsLoading: ((isLoading: boolean) => void) | null;
}>({
  isLoading: false,
  setIsLoading: null,
});

export declare namespace AppLoaderStartType {
  type Props = {
    className?: string;
    config: {
      defaultTheme: ThemeStoreContextType['theme'];
      item: React.ReactNode | JSX.Element;
      type: 'logo' | 'loader';
      color: {
        dark: string;
        light: string;
      };
    };
    children: React.ReactNode;
  };
}

export const AppLoaderStart = observer(
  ({ children, config, className = '' }: AppLoaderStartType.Props) => {
    const { ThemeStore } = useStoreUIV1();
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const currentTheme = ThemeStore.getTheme() || config.defaultTheme;

    useEffect(() => {
      setCSS(
        '--color-primary',
        currentTheme === 'dark' ? config.color.dark : config.color.light,
      );
    }, [config.color, currentTheme]);

    return (
      <AppLoaderContext.Provider value={{ isLoading, setIsLoading }}>
        <div className={`appLoader ${className}`}>
          <div
            style={{
              backgroundColor:
                currentTheme === 'dark'
                  ? ThemeStore.registryColors.base.black
                  : ThemeStore.registryColors.base.white,
            }}
            className={`appLoader__content appLoader__content--${!isLoading ? 'progress' : 'stop'}`}
          >
            {config.type === 'logo' ? (
              <div
                style={{
                  fill:
                    currentTheme === 'light'
                      ? ThemeStore.registryColors.base.black
                      : ThemeStore.registryColors.base.white,
                  color:
                    currentTheme === 'light'
                      ? ThemeStore.registryColors.base.black
                      : ThemeStore.registryColors.base.white,
                }}
                className={`appLoader__content__logo`}
              >
                {config.item}
              </div>
            ) : (
              config.item
            )}
          </div>

          {children}
        </div>
      </AppLoaderContext.Provider>
    );
  },
);

export declare namespace AppLoaderEndType {
  type Props = {
    children: any;
  };
}

export const AppLoaderEnd = ({ children }: AppLoaderEndType.Props) => {
  const { setIsLoading } = useContext(AppLoaderContext);

  useEffect(() => {
    if (setIsLoading) {
      setIsLoading(true);
    }
  }, [setIsLoading]);

  return children ? children : null;
};
